import {
  AttributeMap,
  ClippedGroupOptions,
  FilterResults,
  MaterialType,
  NSAttributeMap,
  SvgIds,
  ViewBox,
} from '@modules/Vector/S/Function/composite/svg.composite.types.ts';
import {
  DEFAULT_FILTER_RESULTS,
  SVG_COMPOSITE_CONSTANTS,
} from '@modules/Vector/S/Constant/composite.constant.ts';
import { ImageUtil } from '@modules/Vector/S/Function/util.image.ts';
import { nanoid } from 'nanoid';
import { copyStrokeAttributes } from '@modules/Vector/S/Function/composite/svg.material-composer.helper.ts';

/**
 * @COMMENT - 정의되지 않은 타입 핸들링을 위한 유틸리티 함수들
 * 안전한 재질 타입 검증 및 기본값 제공
 */
export const validateMaterialType = (type: string): MaterialType => {
  if (type === SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY) {
    return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY;
  }
  if (type === SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY) {
    return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY;
  }
  // 정의되지 않은 타입은 기본값으로 처리
  console.warn(`알 수 없는 재질 타입 '${type}', 기본값 'overlay'로 처리합니다`);
  return SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY;
};

/**
 * @COMMENT - 일반 속성 설정 함수
 * SVG 요소에 일반 속성들을 일괄 설정합니다.
 *
 * 📝 상세 설명:
 * - Object.entries를 활용하여 속성들을 반복적으로 설정
 * - 모든 값들을 String()으로 변환하여 호환성 보장
 * - 코드 중복을 방지하고 가독성을 향상
 */
export const setAttributes = (element: Element, attributes: AttributeMap): void => {
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, String(value));
  });
};

/**
 * @COMMENT - 네임스페이스 속성 설정 함수
 * SVG 요소에 네임스페이스가 있는 속성들을 설정합니다.
 *
 * 📝 상세 설명:
 * - xlink:href 등의 네임스페이스가 필요한 속성들을 처리
 * - SVG 표준 준수를 위해 네임스페이스별로 속성 설정
 * - 복잡한 네임스페이스 속성 설정 로직을 추상화
 */
export const setNSAttributes = (element: Element, nsAttributes: NSAttributeMap): void => {
  Object.entries(nsAttributes).forEach(([namespace, attributes]) => {
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttributeNS(namespace, key, String(value));
    });
  });
};

/**
 * @COMMENT - 다중 요소 일괄 추가 함수
 * 여러 자식 요소들을 부모 요소에 순서대로 추가합니다.
 *
 * 📝 상세 설명:
 * - forEach를 활용하여 배열의 모든 요소들을 순차적으로 추가
 * - 요소 생성과 추가 로직을 분리하여 코드 가독성 향상
 * - SVG 필터 체인 구성 시 유용하게 활용
 */
export const appendChildren = (parent: Element, children: Element[]): void => {
  children.forEach((child) => parent.appendChild(child));
};

/**
 * @COMMENT - 단일 요소 추가 함수
 * 하나의 자식 요소를 부모 요소에 추가합니다.
 *
 * 📝 상세 설명:
 * - 기본 DOM API의 appendChild를 추상화
 * - 함수형 프로그래밍 스타일 유지
 * - 코드 일관성을 위해 wrapper 함수로 제공
 */
export const appendChild = (parent: Element, child: Element): void => {
  parent.appendChild(child);
};

/**
 * @COMMENT - viewBox를 안전하게 파싱하는 헬퍼 함수
 * SVG의 좌표계 정보를 안전하게 추출합니다.
 *
 * 📝 상세 설명:
 * - SVG의 viewBox는 좌표계 변환에 핵심적인 역할을 함
 * - 필터 효과의 위치와 크기를 정확하게 계산하기 위해 필요
 * - viewBox 파싱 실패 시 width/height를 fallback으로 사용
 * - 안전성을 위해 숫자 검증과 예외 처리를 포함
 * - SVG 표준 준수를 위한 좌표계 정보 추출
 */
export const parseViewBox = (svgElement: SVGSVGElement): ViewBox => {
  const viewBoxAttr = svgElement.getAttribute('viewBox');

  if (viewBoxAttr) {
    try {
      const parts = viewBoxAttr.trim().split(/\s+/);
      if (parts.length === 4 && parts[0] && parts[1] && parts[2] && parts[3]) {
        const x = Number(parts[0]);
        const y = Number(parts[1]);
        const width = Number(parts[2]);
        const height = Number(parts[3]);

        if (!isNaN(x) && !isNaN(y) && !isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
          return { x: x || 0, y: y || 0, width, height };
        }
      }
    } catch (error) {
      console.warn('viewBox 파싱 실패:', viewBoxAttr, error);
    }
  }

  // viewBox가 없거나 파싱 실패 시 width/height 사용 (fallback)
  const widthStr = svgElement.getAttribute('width') || '0';
  const heightStr = svgElement.getAttribute('height') || '0';
  const width = parseFloat(widthStr);
  const height = parseFloat(heightStr);

  if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
    throw new Error('viewBox가 없는 경우 SVG는 유효한 width와 height 속성이 있어야 합니다');
  }

  return { x: 0, y: 0, width, height };
};

/**
 * @COMMENT - 신뢰성 향상: 충돌 방지 고유 result 이름 생성
 * 여러 필터가 동시에 실행될 경우를 대비한 고유 result 이름 생성
 *
 * 📝 상세 설명:
 * - 필터 체인에서 각 단계의 result 이름을 고유하게 생성
 * - filterId를 기반으로 네임스페이스 분리하여 충돌 방지
 * - 동시에 여러 SVG 필터가 실행되어도 안전
 * - GPU 메모리에서 텍스처 충돌 방지
 * - 디버깅 시 필터 체인 추적이 용이
 */
export const generateUniqueFilterResults = (filterId?: string): FilterResults => {
  // 필터 ID를 기반으로 고유한 suffix 생성 (충돌 방지)
  const suffix = filterId || nanoid(3);

  return {
    material: `${DEFAULT_FILTER_RESULTS.MATERIAL}-${suffix}`, // 재질 입력 result
    blended: `${DEFAULT_FILTER_RESULTS.BLENDED}-${suffix}`, // 블렌드 결과 result
    final: `${DEFAULT_FILTER_RESULTS.FINAL}-${suffix}`, // 최종 결과 result
  };
};

/**
 * @COMMENT - SVG에서 컷라인 패스를 찾는 함수
 * 스티커의 외곽선을 정의하는 컷라인 패스를 검색합니다.
 *
 * 📝 상세 설명:
 * - 컷라인은 스티커의 외곽선을 정의하는 핵심 요소
 * - stroke-dasharray 속성이 있는 path를 우선 찾음 (점선 스타일 컷라인)
 * - 일반 path를 fallback으로 사용
 * - 컷라인이 없으면 합성 자체가 불가능하므로 null 반환 시 에러 처리
 * - 디자인 패턴 인식 알고리즘 기반 검색
 */
export const findCutPath = (svg: SVGSVGElement): SVGPathElement | null => {
  // stroke-dasharray가 있는 패스를 우선 찾음
  const dashedPath = svg.querySelector(SVG_COMPOSITE_CONSTANTS.SELECTORS.CUT_PATH) as SVGPathElement;
  if (dashedPath) return dashedPath;

  // 일반 path를 찾음
  const path = svg.querySelector('path') as SVGPathElement;
  if (path) return path;

  return null;
};

/**
 * @COMMENT - defs 요소를 생성하거나 기존 요소를 찾는 함수
 * SVG 정의 요소들을 담을 컨테이너를 준비합니다.
 *
 * 📝 상세 설명:
 * - defs는 SVG에서 재사용 가능한 정의 요소들을 담는 컨테이너
 * - 필터, 클립패스, 심볼 등의 재사용 요소들을 한 곳에 모아둠
 * - xmlns:xlink 네임스페이스 추가로 xlink 속성 지원 보장
 * - SVG 표준 준수를 위해 defs를 SVG 시작 부분에 배치
 * - 기존 defs가 없으면 새로 생성하여 추가
 */
export const ensureDefsElement = (svg: SVGSVGElement, doc: Document): SVGDefsElement => {
  let defs = svg.querySelector('defs') as SVGDefsElement;
  if (!defs) {
    defs = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'defs') as SVGDefsElement;
    // defs를 SVG의 첫 번째 자식으로 추가
    const firstChild = svg.firstChild;
    if (firstChild) {
      svg.insertBefore(defs, firstChild);
    } else {
      appendChild(svg, defs);
    }
  }

  // xmlns:xlink 속성 추가
  if (!svg.getAttribute('xmlns:xlink')) {
    setAttributes(svg, {
      'xmlns:xlink': SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK,
    });
  }

  return defs;
};

/**
 * @COMMENT - 컷라인과 클립패스를 생성하는 함수
 * 컷라인을 기반으로 클립패스를 생성하여 재질 마스킹을 준비합니다.
 *
 * 📝 상세 설명:
 * - 컷라인 path를 defs에 정의하여 재사용 가능하게 만듦
 * - 클립패스를 생성하여 재질을 스티커 모양대로 마스킹
 * - use 요소로 컷라인을 참조하여 클립패스 구성
 * - 이렇게 하면 재질 이미지가 스티커 외곽선 내에서만 표시됨
 * - 재사용 가능한 클립패스 생성으로 메모리 효율성 향상
 */
export const createCutAndClipPaths = (
  doc: Document,
  cutPath: SVGPathElement,
  ids: SvgIds,
  defs: SVGDefsElement,
): void => {
  // 컷라인 재사용을 위한 path 정의
  const cutDef = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'path') as SVGPathElement;
  setAttributes(cutDef, {
    id: ids.cut,
    d: cutPath.getAttribute('d') || '',
  });

  // 클립패스 생성
  const clip = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'clipPath') as SVGClipPathElement;
  setAttributes(clip, { id: ids.clip });

  const useForClip = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'use') as SVGUseElement;
  setNSAttributes(useForClip, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      'xlink:href': `#${ids.cut}`,
    },
  });

  // 요소들 defs에 추가 (추상화 적용)
  appendChildren(clip, [useForClip]);
  appendChildren(defs, [cutDef, clip]);
};

/**
 * @COMMENT - 재질 이미지를 데이터 URI로 변환하는 헬퍼 함수
 * 외부 URL 이미지를 SVG 내에서 사용할 수 있는 base64 data URI로 변환합니다.
 *
 * 📝 상세 설명:
 * - 외부 URL 이미지를 base64 data URI로 변환하여 SVG에 embed
 * - SVG 내에서 이미지를 직접 포함하기 위해 필수적인 변환 과정
 * - 브라우저 호환성과 보안성을 위해 data URI 포맷 사용
 * - ImageUtil.urlToDataUri를 활용하여 일관된 변환 로직 유지
 * - CORS 제한 없이 SVG 필터에서 이미지 사용 가능
 */
export const urlToDataUri = async (url: string): Promise<string> => {
  return ImageUtil.urlToDataUri(url);
};

/**
 * @COMMENT - 아웃라인 요소를 추가하는 헬퍼 함수
 * 컷라인을 따라 시각적 외곽선 효과를 추가하여 스티커의 경계를 명확하게 표시합니다.
 *
 * 📝 상세 설명:
 * - 컷라인을 따라 시각적 외곽선 효과 추가로 스티커 경계 명확화
 * - 원본 컷라인의 모든 스트로크 속성을 복사하여 일관된 스타일 유지
 * - vector-effect: non-scaling-stroke로 줌 레벨에 관계없이 일정한 선 굵기 유지
 * - mix-blend-mode: normal로 블렌딩 모드 지정하여 자연스러운 외곽선 효과
 * - use 요소로 컷라인을 재참조하여 DRY 원칙 준수
 */
export const addOutlineElement = (
  doc: Document,
  svg: SVGSVGElement,
  cutPath: SVGPathElement,
  ids: SvgIds,
): void => {
  const outlineTop = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'use') as SVGUseElement;

  // 기본 속성 설정
  setAttributes(outlineTop, {
    fill: 'none',
    'vector-effect': 'non-scaling-stroke',
    style: 'mix-blend-mode:normal',
  });

  // 네임스페이스 속성 설정
  setNSAttributes(outlineTop, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      'xlink:href': `#${ids.cut}`,
    },
  });

  // 스트로크 속성 복사
  const strokeAttributes = copyStrokeAttributes(cutPath);
  setAttributes(outlineTop, strokeAttributes);

  appendChild(svg, outlineTop);
};

/**
 * @COMMENT - 클립 그룹과 필터링된 요소를 생성하는 헬퍼 함수
 * 필터링된 디자인 요소를 클립패스로 마스킹하여 최종 합성 결과를 생성합니다.
 *
 * 📝 상세 설명:
 * - 클립패스로 재질을 스티커 모양대로 정확하게 마스킹
 * - 필터링된 디자인 요소를 그룹으로 묶어 클리핑 적용
 * - isolation:isolate로 필터 효과의 독립성 보장 (다른 요소에 영향 미치지 않음)
 * - 원본 use 요소를 복제하여 필터를 적용한 새 요소 생성
 * - 클립 그룹을 SVG에 추가하여 최종 렌더링 결과 생성
 */

export const createClippedGroup = (
  doc: Document,
  svg: SVGSVGElement,
  ids: SvgIds,
  opts: ClippedGroupOptions = { source: 'design' },
): void => {
  const clipGroup = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'g') as SVGGElement;
  setAttributes(clipGroup, {
    'clip-path': `url(#${ids.clip})`,
    style: 'isolation:isolate',
  });

  let sourceElem: Element | null = null;

  if (opts.source === 'material') {
    // 재질 그룹을 먼저 찾음
    const matGroup = svg.querySelector('g[data-role="material-pass"]') as SVGGElement;
    if (matGroup && matGroup.id) {
      const useMat = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'use') as SVGUseElement;
      setNSAttributes(useMat, {
        [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: { 'xlink:href': `#${matGroup.id}` },
      });
      sourceElem = useMat;
    }
  }

  const img = svg.querySelector('image[id=design-image-svg]') as SVGUseElement;
  img.setAttribute('opacity', '0.9');

  // material 못 찾았거나 source=design → 기본 디자인 use
  if (!sourceElem) {
    const origUse = svg.querySelector(SVG_COMPOSITE_CONSTANTS.SELECTORS.DESIGN_USE) as SVGUseElement;
    if (origUse) sourceElem = origUse.cloneNode(true) as SVGUseElement;
  }

  if (sourceElem) {
    setAttributes(sourceElem, { filter: `url(#${ids.filter})` });
    if (opts.source === 'design') {
      clipGroup.setAttribute('id', `matpass-${ids.filter}`);
      clipGroup.setAttribute('data-role', 'material-pass');
    }
    appendChild(clipGroup, sourceElem);
  }

  appendChild(svg, clipGroup);
};

export const fe = (doc: Document, name: string, attrs: Record<string, string | number>) => {
  const el = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
};

export const pushIf = (parent: Element, node: Element | null | undefined) => {
  if (node) parent.appendChild(node);
};
